'use client';

import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import NextImage from 'next/image';
import { Button } from '@components';

// Components
import SoundWave, { SoundWaveRef } from './SoundWave/SoundWave';
import Shimmer, { ShimmerRef } from '../effects/Shimmer';

// Config
import { ANIMATION, DEBUG_LOGS } from '../config/constants';

// Utils
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { getLinkData } from '@/core/utils';
import { getBooleanParam } from "@/utils/urlParams";

// GSAP
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

// Styles
import styles from './GameOver.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// Assets
import Record from "@/assets/images/record-disc.png";
import IconShare from "@/assets/svg/share.svg";
import IconCheckCircle from "@/assets/svg/check-circle.svg";
import IconArrowTailRightNoPadding from "@/assets/svg/arrow-tail-right-no-padding.svg";

// Debug
import { useDebug } from "@/utils/useDebugLog";

// --------------------

export interface GameOverRef {
  animateIn: () => void;
}

interface GameOverProps {
  gameResult: 'won' | 'lost';
  albumImage: string;
  guessMeterEmoji?: string;
  gameTitle?: string;
  gameNumber?: string;
  guessCount?: string; // e.g. "7/10"
  copy?: {
    headline: string;
    subhead: string;
    body?: string;
    shareButton: string;
    playMoreButton: string;
    shopButton: string;
    shopUrl: string;
    copiedToast: string;
    circleCta?: string;
  };
}

const GameOver = forwardRef<GameOverRef, GameOverProps>(({
  gameResult,
  albumImage,
  guessMeterEmoji,
  gameTitle,
  gameNumber,
  guessCount,
  copy,
}, ref) => {
  const { log } = useDebug({ name: 'GameOver', bgColor: 'yellow', enabled: DEBUG_LOGS });

  // Check for hideShimmer query param
  const hideShimmer = getBooleanParam('hideShimmer');

  // SSR guards
  const { isReady: isViewSizeReady } = useViewSize();
  const { mounted } = useClientMount();

  // Refs
  const rootRef = useRef<HTMLDivElement>(null);
  const albumCoverRef = useRef<HTMLDivElement>(null);
  const shimmerRef = useRef<ShimmerRef>(null);
  const recordRef = useRef<HTMLDivElement>(null);
  const soundWaveWrapperRef = useRef<HTMLDivElement>(null);
  const soundWaveRef = useRef<SoundWaveRef>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subheadRef = useRef<HTMLParagraphElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const buttonRowRef = useRef<HTMLDivElement>(null);
  const toastCopiedRef = useRef<HTMLDivElement>(null);

  const tlAnimateIn = useRef<gsap.core.Timeline | null>(null);

  const hasAnimated = useRef(false);

  // -------------------- Expose animateIn method to parent

  useImperativeHandle(ref, () => ({
    animateIn: () => {
      if (tlAnimateIn.current) {
        // log('animateIn() called from parent');
        tlAnimateIn.current.play();
      }
    }
  }));

  // -------------------- Animation

  // Build animation timeline
  const buildTimeline = () => {
    log('Building animation timeline');

    // Initialize animation timeline (paused - will be played by parent via animateIn())
    tlAnimateIn.current = gsap.timeline({ paused: true }).add('start');

    // Record (disc) slides out
    const recordDuration = 0.6;
    if (recordRef.current) {
      tlAnimateIn.current.add('recordIn', 'start+=0.3');
      tlAnimateIn.current.fromTo(recordRef.current, {
        x: 0,
        rotation: -45,
      }, {
        x: '28%',
        rotation: 0,
        duration: recordDuration,
        ease: 'power1.out'
      }, 'recordIn');
    }

    // ---------- SoundWave

    if (soundWaveWrapperRef.current) {
      tlAnimateIn.current.add('soundWaveIn', 'start+=0.1');

      tlAnimateIn.current.fromTo(soundWaveWrapperRef.current, {
        scaleY: 0,
      }, {
        scaleY: 1,
        duration: 1,
        ease: 'power1.out',
        clearProps: 'transform',
        onStart: () => {
          soundWaveRef.current?.start();
        }
      }, 'soundWaveIn');
    }

    // ---------- Text elements

    // Timing config
    const textElementDuration = 0.4;
    const textElementTimeBacktrack = textElementDuration * 0.85;
    const textElementEase = 'power1.out';
    const textElementDistance = '1.25rem';

    // Headline
    if (headlineRef.current) {
      tlAnimateIn.current.add('headlineIn', '-=0.4');
      tlAnimateIn.current.fromTo(headlineRef.current, {
        autoAlpha: 0,
        y: textElementDistance,
      }, {
        autoAlpha: 1,
        y: 0,
        duration: textElementDuration,
        ease: textElementEase,
      }, 'headlineIn');
    }

    // Subhead
    if (subheadRef.current) {
      tlAnimateIn.current.add('subheadIn', `-=${textElementTimeBacktrack}`);
      tlAnimateIn.current.fromTo(subheadRef.current, {
        autoAlpha: 0,
        y: textElementDistance,
      }, {
        autoAlpha: 1,
        y: 0,
        duration: textElementDuration,
        ease: textElementEase,
      }, 'subheadIn');
    }

    // Body
    if (bodyRef.current) {
      tlAnimateIn.current.add('bodyIn', `-=${textElementTimeBacktrack}`);
      tlAnimateIn.current.fromTo(bodyRef.current, {
        autoAlpha: 0,
        y: textElementDistance,
      }, {
        autoAlpha: 1,
        y: 0,
        duration: textElementDuration,
        ease: textElementEase,
      }, 'bodyIn');
    }

    // Button Row
    if (buttonRowRef.current) {
      tlAnimateIn.current.add('buttonRowIn', `-=${textElementTimeBacktrack}`);
      tlAnimateIn.current.fromTo(buttonRowRef.current, {
        autoAlpha: 0,
        y: textElementDistance,
      }, {
        autoAlpha: 1,
        y: 0,
        duration: textElementDuration,
        ease: textElementEase,
      }, 'buttonRowIn');
    }

    // ---------- Shimmer

    if (shimmerRef.current && !hideShimmer) {
      tlAnimateIn.current.add('shimmer', '-=0.3');
      tlAnimateIn.current.call(() => {
        log('Triggering shimmer animation');
        shimmerRef.current?.shimmer();
      }, [], 'shimmer');
    }

    // Note: Timeline is ready, but paused. Parent will call animateIn() to play it
  };

  // Animate in
  useGSAP(() => {
    log('Game over screen mounted:', gameResult);

    // ---------- Prevent re-running animations

    if (hasAnimated.current) return;
    hasAnimated.current = true;

    // Build the timeline
    buildTimeline();

  }, { dependencies: [], scope: recordRef });

  // -------------------- Buttons

  // Copy emoji to clipboard
  const handleShareClick = async () => {
    if (!guessMeterEmoji) return;

    // Get current URL
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    // Build the full share text
    const shareText = [
      gameNumber ? `CoverStory #${gameNumber}` : 'CoverStory',
      gameTitle ? `"${gameTitle}" ${guessCount || ''}` : '',
      guessMeterEmoji,
      currentUrl
    ].filter(Boolean).join('\n');

    // Check if this is a real mobile device
    const isMobile = typeof navigator !== 'undefined' &&
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) &&
      navigator.maxTouchPoints > 0;

    // Mobile: Use native share sheet if available on real mobile devices
    if (isMobile && typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'Rock Hall Game',
          text: shareText,
        });
        log('Shared via native share sheet');
        return;
      } catch (err) {
        // User cancelled or share failed - do nothing
        return;
      }
    }

    // Desktop: Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      log('Share text copied to clipboard:', shareText);

      // Animate toast
      if (toastCopiedRef.current) {
        const tl = gsap.timeline();

        // Fade in
        tl.fromTo(toastCopiedRef.current, {
          autoAlpha: 0,
          y: 20,
        }, {
          autoAlpha: 1,
          y: 0,
          duration: 0.2,
          ease: 'power2.out',
        });

        // Hold
        tl.to({}, { duration: 1.5 });

        // Fade out
        tl.to(toastCopiedRef.current, {
          autoAlpha: 0,
          y: 20,
          duration: 0.2,
          ease: 'power2.in',
        });
      }
    } catch (err) {
      log('Failed to copy share text:', err);
    }
  };

  // Handle "Play More" button click
  const handlePlayMoreClick = () => {
    // Scroll down the page to the list cover stories section
    const listSection = document.querySelector('[class*="list-cover-stories"]');
    log('Play More button clicked', { listSection });
    if (listSection) {
      listSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Prepare shop button data
  const shopButtonData = (copy?.shopUrl && copy.shopUrl.trim() !== '') ? getLinkData({
    linkType: ['external'],
    label: copy.shopButton || 'SHOP THE VINYL',
    externalLink: copy.shopUrl
  }) : null;

  // -------------------- Render

  if (!mounted || !isViewSizeReady) return null;

  return (
    <div className={cx('game-over', gameResult)} ref={rootRef}>

      {/* Top section */}
      <div className={cx('game-over-top')}>

        {/* Sound Wave Background (animated bars) */}
        <div className={cx('sound-wave-wrapper')} ref={soundWaveWrapperRef}>
          <SoundWave ref={soundWaveRef} animationDuration={ANIMATION.GAME_OVER_SOUNDWAVE_DUR} />
        </div>

        {/* Album */}
        <div className={cx('album-cover')} ref={albumCoverRef}>
          {/* Record (slides out from behind album) */}
          <div ref={recordRef} className={cx('record')}>
            <NextImage src={Record} alt="Vinyl Record" />
          </div>
          {/* Album Cover */}
          <Shimmer ref={shimmerRef}>
            <img src={albumImage} alt="Album cover" className={cx('album-cover-image')} />
          </Shimmer>
        </div>

      </div>

      {/* Bottom section */}
      <div className={cx('game-over-bottom')}>

        {/* Headline */}
        <h2 className={cx('title-uc-s', 'headline')} ref={headlineRef}>
          {copy?.headline}
        </h2>

        {/* Subhead */}
        <p className={cx('text-m', 'subhead')} ref={subheadRef}>
          {copy?.subhead}
        </p>

        {/* Body (optional) */}
        {copy?.body && (
          <p className={cx('text-m', 'body')} ref={bodyRef}>
            {copy.body}
          </p>
        )}

        {/* Buttons */}
        <div className={cx('button-row')} ref={buttonRowRef} id="game-over-button-row">

          {/* Share Button */}
          <Button
            label={copy?.shareButton || 'SHARE'}
            onClick={handleShareClick}
            icon={IconShare}
          />

          {/* Play More Button */}
          <Button
            label={copy?.playMoreButton || 'PLAY MORE'}
            onClick={handlePlayMoreClick}
            icon={IconArrowTailRightNoPadding}
            className={cx('play-more-button')}
          />

          {/* Shop Button */}
          {shopButtonData && <Button {...shopButtonData} />}

        </div>

        {/* Copied Toast */}
        <div className={cx('toast-copied')}
          ref={toastCopiedRef}
          aria-hidden="true"
        >
          <IconCheckCircle /> <span className={cx('text-m')}>{copy?.copiedToast}</span>
        </div>

      </div>

    </div>
  );

});

GameOver.displayName = 'GameOver';

export default GameOver;
